import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { Mail, Linkedin, Github, Instagram } from 'lucide-react'
import talkidaLogo from '../../assets/icons/logo.png'

const LandingFooter = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const currentYear = new Date().getFullYear()

    const scrollToSection = (href) => {
        // Check if it's a route (starts with /)
        if (href.startsWith('/')) {
            navigate(href)
            return
        }

        // If it's an anchor link (starts with #)
        if (href.startsWith('#')) {
            // If we're not on the landing page, navigate to landing page first
            if (location.pathname !== '/') {
                navigate(`/${href}`)
                // Wait for navigation and DOM update, then scroll
                setTimeout(() => {
                    const element = document.querySelector(href)
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' })
                    }
                }, 100)
            } else {
                // We're already on landing page, just scroll
                const element = document.querySelector(href)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            }
            return
        }
    }

    // Handle scroll after navigation (when coming from another page)
    useEffect(() => {
        if (location.hash) {
            setTimeout(() => {
                const element = document.querySelector(location.hash)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            }, 300)
        }
    }, [location])

    const footerSections = [
        {
            title: 'Product',
            links: [
                { label: 'About', href: '/about' },
                { label: 'Features', href: '#features' },
                { label: 'Pricing', href: '#pricing' },
                { label: 'How It Works', href: '#how-it-works' }
            ]
        },
        {
            title: 'Resources',
            links: [
                { label: 'Documentation', href: 'https://cdn.talkida.co.in/', external: true },
                { label: 'Tutorials', href: 'https://cdn.talkida.co.in/', external: true }
            ]
        }
        // Commented sections for future use:
        // {
        //   title: 'Company',
        //   links: [
        //     { label: 'About Us', href: '#about' },
        //     { label: 'Blog', href: '#blog' },
        //     { label: 'Careers', href: '#careers' },
        //     { label: 'Contact', href: '#contact' }
        //   ]
        // },
        // {
        //   title: 'Legal',
        //   links: [
        //     { label: 'Privacy Policy', href: '#privacy' },
        //     { label: 'Terms of Service', href: '#terms' },
        //     { label: 'Cookie Policy', href: '#cookies' },
        //     { label: 'GDPR', href: '#gdpr' }
        //   ]
        // },
        // {
        //   title: 'Community',
        //   links: [
        //     { label: 'Community Forum', href: '#community' }
        //   ]
        // },
        // {
        //   title: 'Support',
        //   links: [
        //     { label: 'Help Center', href: '#support' },
        //     { label: 'Contact Support', href: '#support' }
        //   ]
        // }
    ]

    const socialLinks = [
        { icon: Linkedin, href: 'https://www.linkedin.com/company/talkida-ai', label: 'LinkedIn' },
        { icon: Github, href: 'https://github.com/talkida/', label: 'GitHub' },
        { icon: Instagram, href: 'https://www.instagram.com/talkida.co.in/', label: 'Instagram' }
    ]

    return (
        <footer className="relative bg-slate-900/50 backdrop-blur-xl border-t border-white/10 pt-20 pb-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Main Footer Content */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {/* Brand Column */}
                    <div className="col-span-1">
                        <div
                            className="flex items-center gap-3 mb-4 cursor-pointer"
                            onClick={() => navigate('/')}
                        >
                            <img src={talkidaLogo} alt="Talkida" className="h-10 w-auto" />
                            <span className="text-2xl font-bold text-white">Talkida Technologies</span>
                        </div>
                        <p className="text-white/60 mb-6 max-w-sm">
                            The easiest way to add AI-powered chatbots to your website.
                            Plug-n-Play integration in 5 minutes.
                        </p>

                        {/* Social Links */}
                        <div className="flex items-center gap-4">
                            {socialLinks.map((social) => (
                                <motion.a
                                    key={social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-all duration-300 border border-white/10 hover:border-purple-500/50"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    aria-label={social.label}
                                >
                                    <social.icon className="w-5 h-5" />
                                </motion.a>
                            ))}
                        </div>
                    </div>

                    {/* Footer Links */}
                    {footerSections.map((section, index) => (
                        <div key={section.title}>
                            <h3 className="text-white font-semibold mb-4">{section.title}</h3>
                            <ul className="space-y-3">
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            target={link.external ? "_blank" : undefined}
                                            rel={link.external ? "noopener noreferrer" : undefined}
                                            onClick={(e) => {
                                                if (!link.external) {
                                                    e.preventDefault()
                                                    scrollToSection(link.href)
                                                }
                                            }}
                                            className="text-white/60 hover:text-white transition-colors duration-200 text-sm cursor-pointer"
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/10 pt-8 mt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="text-white/60 text-sm text-center md:text-left">
                            © {currentYear} Talkida Technologies. All rights reserved.
                        </div>

                        <div className="flex items-center gap-6 text-sm">
                            <a href="mailto:info@talkida.co.in" className="text-white/60 hover:text-white transition-colors flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                Contact
                            </a>
                        </div>
                    </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 pt-8 border-t border-white/10">
                    <div className="flex flex-wrap justify-center items-center gap-8 text-white/40 text-xs">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span>All Systems Operational</span>
                        </div>
                        <span>•</span>
                        <span>MSME Registered</span>
                        <span>•</span>
                        <span>ISTART Certified</span>
                        <span>•</span>
                        <span>SOC 2 Certified</span>
                        <span>•</span>
                        <span>GDPR Compliant</span>
                        <span>•</span>
                        <span>256-bit Encryption</span>
                    </div>
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute -bottom-32 -left-32 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.1, 0.2, 0.1],
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl"
                    animate={{
                        scale: [1.2, 1, 1.2],
                        opacity: [0.2, 0.1, 0.2],
                    }}
                    transition={{ duration: 8, repeat: Infinity, delay: 1 }}
                />
            </div>
        </footer>
    )
}

export default LandingFooter

